import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Upload, Loader2, X, Trash2, Info, Camera, Link, ExternalLink, Plus } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { showToast } from '@/components/ui/Toast';
import Cookies from 'js-cookie';
import imageCompression from 'browser-image-compression';

interface DriveLink {
    asset_id: number;
    asset_type: number;
    asset: string;
}

interface GalleryData {
    images: Array<{
        asset_id: number;
        asset_type: number;
        asset: string;
    }>;
    drive_links: DriveLink[];
}

const Gallery: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<GalleryData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [uploadingImages, setUploadingImages] = useState(false);
    const [deletingItem, setDeletingItem] = useState<number | null>(null);
    const [newDriveLink, setNewDriveLink] = useState('');
    const [addingDriveLink, setAddingDriveLink] = useState(false);
    const [deletingDriveLink, setDeletingDriveLink] = useState<number | null>(null);

    const CDN_BASE_URL = 'https://yellowslate.sgp1.cdn.digitaloceanspaces.com';

    useEffect(() => {
        fetchGalleryData();
    }, [id]);

    const fetchGalleryData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiRequest(`/media/${id}`);
            if (response.success && response.data && response.data.gallery) {
                setData({ images: response.data.gallery.images, drive_links: response.data.gallery.drive_links || [] });
            } else {
                setError(response.message || 'Failed to load gallery information');
            }
        } catch (err: any) {
            console.error('Error fetching gallery data:', err);
            setError('Failed to load gallery information');
        } finally {
            setLoading(false);
        }
    };

    const [compressing, setCompressing] = useState(false);

    const handleGalleryFilesSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        // Check total count
        const currentCount = data?.images.length || 0;
        const newTotal = currentCount + selectedFiles.length + files.length;

        if (newTotal > 25) {
            showToast(`Gallery limit exceeded. You can only have 25 images total. Current: ${currentCount}, Selected: ${selectedFiles.length + files.length}`, 'error');
            return;
        }

        // Validate file types
        const imageFiles = files.filter(file => {
            if (!file.type.startsWith('image/')) {
                showToast(`${file.name} is not an image file`, 'error');
                return false;
            }
            return true;
        });

        if (imageFiles.length === 0) return;

        // Process files - compress if needed
        setCompressing(true);
        const maxSizeBytes = 1024 * 1024; // 1MB
        const processedFiles: File[] = [];

        for (const file of imageFiles) {
            if (file.size > maxSizeBytes) {
                try {
                    showToast(`Compressing ${file.name}...`, 'info');
                    const options = {
                        maxSizeMB: 1,
                        maxWidthOrHeight: 1200,
                        useWebWorker: true,
                        fileType: file.type as string,
                    };
                    const compressedFile = await imageCompression(file, options);
                    const originalSizeKB = (file.size / 1024).toFixed(1);
                    const compressedSizeKB = (compressedFile.size / 1024).toFixed(1);
                    showToast(`${file.name}: ${originalSizeKB}KB → ${compressedSizeKB}KB`, 'success');
                    processedFiles.push(compressedFile);
                } catch (err) {
                    console.error('Error compressing image:', err);
                    showToast(`Failed to compress ${file.name}`, 'error');
                }
            } else {
                processedFiles.push(file);
            }
        }

        setCompressing(false);

        if (processedFiles.length > 0) {
            setSelectedFiles([...selectedFiles, ...processedFiles]);
        }
    };

    const removeSelectedFile = (index: number) => {
        setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
    };

    const uploadGalleryImages = async () => {
        if (selectedFiles.length === 0) return;

        try {
            setUploadingImages(true);
            setError(null);

            const formData = new FormData();
            selectedFiles.forEach(file => {
                formData.append('files', file);
            });

            // Get auth token from cookie
            const token = Cookies.get('crm_auth_token');

            // Build headers
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // Use fetch directly for FormData upload (don't set Content-Type, browser will set it)
            const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
            const response = await fetch(`${API_BASE_URL}/media/${id}/gallery/images`, {
                method: 'POST',
                headers,
                credentials: 'include',
                body: formData,
            });

            const responseData = await response.json();

            if (response.ok && (responseData.success || responseData.status === 'success')) {
                showToast(responseData.data?.message || responseData.message || 'Images uploaded successfully!', 'success');

                if (responseData.data?.errors && responseData.data.errors.length > 0) {
                    responseData.data.errors.forEach((err: any) => {
                        showToast(`${err.filename}: ${err.error}`, 'error');
                    });
                }

                await fetchGalleryData();
                setSelectedFiles([]);
            } else {
                throw new Error(responseData.message || 'Failed to upload images');
            }
        } catch (err: any) {
            console.error('Error uploading gallery images:', err);
            const errorMessage = err.message || 'Failed to upload gallery images';
            setError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setUploadingImages(false);
        }
    };

    const handleDeleteGalleryItem = async (assetId: number) => {
        if (!confirm('Are you sure you want to delete this image?')) return;

        try {
            setDeletingItem(assetId);
            setError(null);

            const response = await apiRequest(`/media/${id}/gallery/${assetId}`, {
                method: 'DELETE',
            });

            if (response.success) {
                showToast('Image deleted successfully!', 'success');
                await fetchGalleryData();
            } else {
                throw new Error(response.message || 'Failed to delete image');
            }
        } catch (err: any) {
            console.error('Error deleting image:', err);
            const errorMessage = err.message || 'Failed to delete image';
            setError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setDeletingItem(null);
        }
    };

    const handleAddDriveLink = async () => {
        if (!newDriveLink.trim()) {
            showToast('Please enter a drive link URL', 'error');
            return;
        }
        try {
            setAddingDriveLink(true);
            const response = await apiRequest(`/media/${id}/gallery/drive-link`, {
                method: 'POST',
                body: { url: newDriveLink.trim() },
            });
            if (response.success) {
                showToast('Drive link added!', 'success');
                setNewDriveLink('');
                await fetchGalleryData();
            } else {
                showToast(response.message || 'Failed to add drive link', 'error');
            }
        } catch (err: any) {
            showToast(err.message || 'Failed to add drive link', 'error');
        } finally {
            setAddingDriveLink(false);
        }
    };

    const handleDeleteDriveLink = async (assetId: number) => {
        if (!confirm('Remove this drive link?')) return;
        try {
            setDeletingDriveLink(assetId);
            const response = await apiRequest(`/media/${id}/gallery/${assetId}`, {
                method: 'DELETE',
            });
            if (response.success) {
                showToast('Drive link removed!', 'success');
                await fetchGalleryData();
            } else {
                showToast(response.message || 'Failed to remove drive link', 'error');
            }
        } catch (err: any) {
            showToast(err.message || 'Failed to remove drive link', 'error');
        } finally {
            setDeletingDriveLink(null);
        }
    };

    const getImageUrl = (urlOrFilename: string) => {
        // If it's already a complete URL, return it as-is
        if (urlOrFilename.startsWith('http://') || urlOrFilename.startsWith('https://')) {
            return urlOrFilename;
        }

        // Otherwise, construct the URL (for backward compatibility with old data)
        return `${CDN_BASE_URL}/gallery/${urlOrFilename}`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 text-brandColor animate-spin" />
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="p-8">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-800 dark:text-red-200">{error}</p>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="p-8">
                <p className="text-gray-500">No data available</p>
            </div>
        );
    }

    const currentImageCount = data.images.length;
    const remainingSlots = 25 - currentImageCount;

    return (
        <div className="h-full bg-white dark:bg-gray-900">
            {/* Page Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-8 py-4">
                <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                        <div className="p-2 bg-brandColor rounded-lg text-white">
                            <Camera className="w-6 h-6" />
                        </div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Photo Gallery</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Upload and manage school photos (Maximum 25 images, 1MB each)</p>
                    </div>
                </div>
            </div>

            {/* Auto-save notice */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 px-8 py-3">
                <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                        Selected images will be uploaded when you click "Upload All" button
                    </p>
                </div>
            </div>

            {error && (
                <div className="mx-8 mt-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-800 dark:text-red-200">{error}</p>
                </div>
            )}

            <div className="p-8 space-y-8">
                {/* Drive Links */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-brandColor px-6 py-3">
                        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                            <Link className="w-5 h-5" />
                            Drive Links
                        </h2>
                        <p className="text-xs text-white/80 mt-1">Add one or more Google Drive links for additional gallery assets</p>
                    </div>
                    <div className="p-6 space-y-4">
                        {/* Add new drive link */}
                        <div className="flex gap-2">
                            <input
                                type="url"
                                value={newDriveLink}
                                onChange={(e) => setNewDriveLink(e.target.value)}
                                placeholder="https://drive.google.com/drive/folders/..."
                                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor dark:bg-gray-700 dark:text-white text-sm"
                                onKeyPress={(e) => { if (e.key === 'Enter') handleAddDriveLink(); }}
                            />
                            <button
                                onClick={handleAddDriveLink}
                                disabled={addingDriveLink || !newDriveLink.trim()}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-brandColor hover:bg-brand-hover text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                                {addingDriveLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                {addingDriveLink ? 'Adding...' : 'Add Link'}
                            </button>
                        </div>

                        {/* Saved drive links list */}
                        {data.drive_links.length > 0 && (
                            <div className="space-y-2">
                                {data.drive_links.map((link) => (
                                    <div
                                        key={link.asset_id}
                                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-brandColor transition-colors"
                                    >
                                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                                            <ExternalLink className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                            <a
                                                href={link.asset}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm text-brandColor hover:underline truncate"
                                            >
                                                {link.asset}
                                            </a>
                                        </div>
                                        <button
                                            onClick={() => handleDeleteDriveLink(link.asset_id)}
                                            disabled={deletingDriveLink === link.asset_id}
                                            className="ml-2 p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                                        >
                                            {deletingDriveLink === link.asset_id ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Trash2 className="w-4 h-4" />
                                            )}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {data.drive_links.length === 0 && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">No drive links added yet.</p>
                        )}
                    </div>
                </div>

                {/* Bulk Image Upload */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-brandColor px-6 py-3">
                        <h2 className="text-lg font-semibold text-white">Upload Images</h2>
                        <p className="text-xs text-white/80 mt-1">
                            Maximum 25 images total. {remainingSlots} slots remaining. Max 1MB per image.
                        </p>
                    </div>
                    <div className="p-6 space-y-4">
                        {/* File Input */}
                        <div>
                            <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 transition-colors ${compressing ? 'opacity-50 cursor-wait' : ''}`}>
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                    {compressing ? (
                                        <>
                                            <Loader2 className="w-8 h-8 mb-2 text-brandColor animate-spin" />
                                            <p className="mb-2 text-sm text-brandColor font-semibold">
                                                Compressing images...
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-8 h-8 mb-2 text-gray-500 dark:text-gray-400" />
                                            <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                                <span className="font-semibold">Click to upload</span> or drag and drop
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">PNG, JPG, JPEG (MAX. 1MB each)</p>
                                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">✓ Large images will be automatically compressed</p>
                                        </>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    className="hidden"
                                    multiple
                                    accept="image/*"
                                    onChange={handleGalleryFilesSelect}
                                    disabled={remainingSlots === 0 || compressing}
                                />
                            </label>
                            {remainingSlots === 0 && (
                                <p className="text-sm text-red-500 mt-2">Gallery is full. Delete some images to upload more.</p>
                            )}
                        </div>

                        {/* Selected Files Preview */}
                        {selectedFiles.length > 0 && (
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                                        Selected Files ({selectedFiles.length})
                                    </h3>
                                    <button
                                        onClick={uploadGalleryImages}
                                        disabled={uploadingImages}
                                        className="inline-flex items-center px-3 py-1.5 bg-brandColor hover:bg-brand-hover text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {uploadingImages && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                        {uploadingImages ? 'Uploading...' : 'Upload All'}
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                                    {selectedFiles.map((file, index) => (
                                        <div key={index} className="relative group">
                                            <img
                                                src={URL.createObjectURL(file)}
                                                alt={file.name}
                                                className="w-full h-24 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                                            />
                                            <button
                                                onClick={() => removeSelectedFile(index)}
                                                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                                                {file.name}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Current Gallery Images */}
                {data.images.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="bg-brandColor px-6 py-3">
                            <h2 className="text-lg font-semibold text-white">Gallery Images ({data.images.length}/25)</h2>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                {data.images.map((image) => (
                                    <div key={image.asset_id} className="relative group">
                                        <img
                                            src={getImageUrl(image.asset)}
                                            alt="Gallery"
                                            className="w-full h-32 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                                        />
                                        <button
                                            onClick={() => handleDeleteGalleryItem(image.asset_id)}
                                            disabled={deletingItem === image.asset_id}
                                            className="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                                        >
                                            {deletingItem === image.asset_id ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Trash2 className="w-4 h-4" />
                                            )}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {data.images.length === 0 && selectedFiles.length === 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="bg-brandColor px-6 py-3">
                            <h2 className="text-lg font-semibold text-white">Gallery Images (0/25)</h2>
                        </div>
                        <div className="p-6">
                            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                                No images uploaded yet. Use the upload section above to add images.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Gallery;
